// app/frontend/pages/Posts/PostShow.tsx

import { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import {
  MantineProvider,
  Container,
  Title,
  Text,
  Image,
  AspectRatio,
  Group,
  Badge,
  Stack,
  Divider,
  Box,
  Anchor,
  Avatar,
  Paper,
  Grid,
  SimpleGrid,
  Button,
} from '@mantine/core';
import { Head, usePage, Link } from '@inertiajs/react';

import AboutAuthor from '../../components/Posts/AboutAuthor';
import InteractionButtons from '../../components/Posts/InteractionButtons';
import CommentsForm from '../../components/Posts/CommentsForm';
import PodcastPlayer from '../../components/Posts/PodcastPlayer';
import CommentsList from '../../components/Posts/CommentsList';
import EventCard from '../../components/Cards/EventCard';
import ContentPostCard from '../../components/Cards/ContentPostCard';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import PostDrawerContent from '../../components/Drawer/PostDrawerContent';
import { CommentData, CommentFormData } from '../../types/comment';

import { IconArrowLeft, IconPencil, IconMicrophone, IconPlayerPlay, IconLibraryPhoto } from '@tabler/icons-react';

interface PostType {
  id: number;
  name: string;
}
interface DanceStyle {
  id: number;
  name: string;
}
interface Author {
  id: number;
  name: string;
  avatar_url?: string;
  claimed_artist?: { id: number; name: string; slug: string };
}
interface Comment {
  id: number;
  body: string;
  user: { id: number; name: string; avatar_url?: string };
  children?: Comment[];
}
interface Event {
  id: number;
  name: string;
  slug: string /* …*/;
}
interface Post {
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
  id: number;
  title: string;
  slug: string;
  body: string; // HTML
  excerpt: string;
  published_at: string;
  featured_image_url?: string;
  video_url?: string;
  podcast_id?: string;
  post_types: PostType[];
  postTypeIcons?: React.ReactNode;
  dance_styles: DanceStyle[];
  author: Author;
  likes_count: number;
  liked: boolean;
  comments_count: number;
  comments: Comment[];
  related_posts: Post[];
  related_events: Event[];
  artist_related_events: Event[];
  tags: { id: number; name: string }[];
}

interface PageProps {
  post: Post;
  stripePublishableKey?: string;
}

export default function PostShow() {
  // Get the post data from Inertia props
  const { post, stripePublishableKey, page_meta } = usePage().props as PageProps;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<'post' | 'event' | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const postTypeIconMap: Record<string, React.ReactNode> = {
    Blog: <IconPencil size={12} />,
    Podcast: <IconMicrophone size={12} />,
    Video: <IconPlayerPlay size={12} />,
    Carousel: <IconLibraryPhoto size={12} />,
  };

  console.log('Video URL:', post.video_url);

  const contentRef = useRef<HTMLDivElement>(null);

  const handleOpenDrawer = (type: 'post' | 'event', item: any) => {
    setSelectedItem(item);
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedItem(null);
  };

  // Auth state - replace with your actual auth implementation
  const isUserSignedIn = true; // Example - replace with actual auth check

  const handleCommentSubmit = async (formData: CommentFormData) => {
    try {
      // Here you would typically make an API call to save the comment
      // For example:
      /*
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit comment');
      }
      
      // Refresh the page or update comments state
      window.location.reload();
      */

      console.log('Comment submitted:', formData);
      // Temporarily just log the comment - replace with actual API call
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  };

  // Utility to check if Post has type
  const hasType = (name: string) => post.post_types.some((pt) => pt.name === name);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';

    // Extract video ID from various YouTube URL formats
    let videoId = '';

    // Format: https://youtu.be/VIDEO_ID
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1];
    }
    // Format: https://www.youtube.com/watch?v=VIDEO_ID
    else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    }
    // Format: https://www.youtube.com/embed/VIDEO_ID
    else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('youtube.com/embed/')[1];
    }

    // Remove any additional parameters
    if (videoId && videoId.includes('&')) {
      videoId = videoId.split('&')[0];
    }

    // Return embed URL if video ID was found
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }
    // console.log('Post Page meta:', page_meta);
    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach((link) => {
        // Set target="_blank" to open in new tab
        link.setAttribute('target', '_blank');

        // For better security, also add rel="noopener noreferrer"
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, []);

  return (
    <MantineProvider>
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />

          {/* Open Graph tags */}
          <meta property="og:title" content={page_meta.title} />
          <meta property="og:description" content={page_meta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page_meta.url} />
          <meta property="og:image" content={page_meta.image} />

          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={page_meta.title} />
          <meta name="twitter:description" content={page_meta.description} />
          <meta name="twitter:image" content={page_meta.image} />
        </Helmet>
      )}
      <Head title={`${post.title} | ${post.post_types.map((pt) => pt.name).join(', ')}`} />

      <Container size="lg" py="xl">
        {/* Header */}
        <Stack gap="4px">
          <Group align="flex-start" justify="flex-start" mb="0">
            {post.post_types.map((pt) => (
              <Badge
                key={pt.id}
                variant="filled"
                color="rgba(0, 0, 0, 0.63)"
                size="lg"
                className="poppins"
                leftSection={postTypeIconMap[pt.name]}
              >
                {pt.name}
              </Badge>
            ))}
            <Title className="rubik-header" style={{ lineHeight: 1 }}>
              {post.title}
            </Title>
          </Group>

          {/* Featured Media */}
          {hasType('Video') && post.video_url ? (
            <AspectRatio ratio={16 / 9} mb="4px">
              <div className="video-post">
                {/* Fix 2: Call getYouTubeEmbedUrl directly instead of using embedUrl */}
                {getYouTubeEmbedUrl(post.video_url) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(post.video_url)}
                    frameBorder="0"
                    width="100%"
                    height="100%"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <p>Invalid YouTube URL</p>
                )}
              </div>
            </AspectRatio>
          ) : post.featured_image_url ? (
            <Image src={post.featured_image_url} alt={post.title} radius="md" mb="4px" />
          ) : (
            <Text c="dimmed">No image available for this post.</Text>
          )}
          <Text c="dimmed" className="mb-1">
            Published on{' '}
            {new Date(post.published_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Text>

          {/* Podcast Player */}
          {hasType('Podcast') && post.podcast_id && <PodcastPlayer podcastId={post.podcast_id} mb="md" />}

          {/* Author (mobile) */}
          <Box display={{ base: 'block', md: 'none' }}>
            <AboutAuthor post={post} stripePublishableKey={stripePublishableKey} isUserSignedIn={isUserSignedIn} />
          </Box>

          {/* Interactions */}
          <InteractionButtons likeCount={post.likes_count} liked={post.liked} context="post" objectId={post.id} />
        </Stack>

        {/* Main Content + Sidebar Layout */}
        <Group align="flex-start" gap="sm" mt="md">
          {/* Main */}
          <Box style={{ flex: 2 }}>
            <Paper
              shadow="lg"
              p={30}
              mb="md"
              radius="md"
              style={{
                width: '100%',
                margin: '0 auto',
                backgroundColor: '#17171790',
              }}
            >
              <div
                className="event-description text-white"
                dangerouslySetInnerHTML={{ __html: post.body }}
                ref={contentRef}
              />
            </Paper>

            {/* Comments */}
            <Box id="comments" my="xl">
              <Title size="lg" fw="bold" className="poppins text-white mb-2">
                Comments ({post.comments_count})
              </Title>

              <CommentsList
                postId={post.id}
                comments={post.comments.map((comment: Comment) => ({
                  ...comment,
                  created_at: comment.created_at || new Date().toISOString(),
                  // Map children to comments for consistency
                  comments: comment.children?.map((child) => ({
                    ...child,
                    created_at: child.created_at || new Date().toISOString(),
                  })),
                }))}
                isUserSignedIn={isUserSignedIn}
                onCommentSubmit={handleCommentSubmit}
              />
              <CommentsForm postId={post.id} isUserSignedIn={isUserSignedIn} onCommentSubmit={handleCommentSubmit} />
            </Box>

            {/* Back link */}
            <Button
              component={Link}
              href="/posts"
              size="md"
              leftSection={<IconArrowLeft size={16} />}
              variant="filled"
              color="#EA580C"
              style={{
                '&:hover': {
                  backgroundColor: '#EA580C',
                },
                fontWeight: 'bold',
                marginBottom: '2rem',
                marginTop: '1rem',
              }}
            >
              Back to All Posts
            </Button>

            {/* Related Posts */}
            {post.related_posts.length > 0 && (
              <Box mt="xl">
                <Title size="lg" fw="bold" className="poppins text-white mb-2">
                  More from {post.author.name}
                </Title>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="8px">
                  {post.related_posts.map((rp) => (
                    <ContentPostCard key={rp.id} post={rp} onCardClick={() => handleOpenDrawer('post', rp)} />
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Related Events by Artists */}
            {post.artist_related_events.length > 0 && (
              <Box mt="xl">
                <Title size="lg" fw="bold" className="poppins text-white">
                  Related Events by Linked Artists
                </Title>
                <Grid gutter="md" mt="md">
                  {post.artist_related_events.map((ev) => (
                    <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={ev.id}>
                      <EventCard event={ev} onCardClick={() => handleOpenDrawer('event', ev)} />
                    </Grid.Col>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Related Events by Dance Styles */}
            {post.related_events.length > 0 && (
              <Box mt="xl">
                <Title size="lg" fw="bold" className="poppins text-white">
                  Related Events by Dance Style(s)
                </Title>
                <Grid gutter="md" mt="md">
                  {post.related_events.map((ev) => (
                    <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={ev.id}>
                      <EventCard event={ev} onCardClick={() => handleOpenDrawer('event', ev)} />
                    </Grid.Col>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>

          {/* Sidebar */}
          <Box style={{ flex: 1, display: { base: 'none', lg: 'block' } }}>
            <AboutAuthor author={post.author} mb="lg" />

            <Box mb="lg">
              <Title size="lg" fw="bold" className="poppins text-white">
                Dance Styles
              </Title>
              <Group mt="xs">
                {post.dance_styles.map((ds) => (
                  <Badge key={ds.id} color="#EA580C" size="md" className="tag-badge">
                    {ds.name}
                  </Badge>
                ))}
              </Group>
            </Box>

            {post.tags.length > 0 && (
              <Box mb="lg">
                <Title order={4}>Tags</Title>
                <Group mt="xs">
                  {post.tags.map((t) => (
                    <Badge key={t.id} variant="outline">
                      {t.name}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}
          </Box>
        </Group>
      </Container>
      {drawerType && selectedItem && (
        <DynamicDrawer opened={drawerOpen} type={drawerType} data={selectedItem} onClose={handleCloseDrawer} />
      )}
    </MantineProvider>
  );
}
